import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  PICKLE_BASES, REGIONAL_STYLES, TEXTURES, STUDIO_INGREDIENTS,
  JAR_SIZES, PROFILES, QUICK_PRESETS, getLevelLabel,
} from "@/lib/constants";
import {
  Sparkles, ChefHat, Heart, Flame, Droplets, Wind, Cherry,
  Baby, HeartHandshake, Activity, HeartPulse, ShieldCheck, RotateCcw, Info,
} from "lucide-react";

const profileIconMap = { Baby, HeartHandshake, Activity, HeartPulse, ShieldCheck };

const INGREDIENT_CATEGORIES = ["Aromatics", "Seeds", "Fresh", "Oils", "Heat", "Sweet", "Nuts", "Souring"];

const sectionAnim = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.5 },
};

/** Animated description shelf shown below option groups */
const InfoShelf = ({ name, description }) => (
  <AnimatePresence mode="wait">
    {description && (
      <motion.div
        key={description}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 6 }}
        transition={{ duration: 0.18 }}
        className="mt-3 flex items-start gap-2.5 rounded-xl bg-cream-secondary px-4 py-3"
      >
        <Info size={13} className="text-earth-green shrink-0 mt-0.5" />
        <p className="text-xs text-sw-text-secondary leading-relaxed">
          {name && <span className="font-semibold text-sw-text">{name} — </span>}
          {description}
        </p>
      </motion.div>
    )}
  </AnimatePresence>
);

const Section = ({
  id, step, title, subtitle, icon: Icon, onView, children,
  isMobileActive, isFirstStep, isLastStep, onNextStep, onPrevStep,
}) => (
  <motion.section
    id={id}
    {...sectionAnim}
    onViewportEnter={() => onView?.(step)}
    className={`${isMobileActive ? "block" : "hidden"} scroll-mt-28 rounded-3xl border border-gray-100 bg-white p-5 shadow-sm lg:block lg:scroll-mt-32 lg:p-7`}
    data-testid={`studio-section-${id}`}
  >
    <div className="mb-5 flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-earth-green/10">
            <Icon size={18} className="text-earth-green" />
          </div>
        )}
        <div>
          <h3 className="font-heading font-700 text-sw-text text-base">{title}</h3>
          {subtitle && <p className="text-xs text-sw-text-muted">{subtitle}</p>}
        </div>
      </div>
      <span className="rounded-full bg-cream-secondary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-earth-green lg:hidden">
        {String(step + 1).padStart(2, "0")}
      </span>
    </div>
    {children}

    {!isLastStep && (
      <div className="mt-5 flex items-center gap-2 border-t border-gray-100 pt-4 lg:hidden">
        {!isFirstStep && (
          <button type="button" onClick={onPrevStep}
            className="rounded-full border border-gray-200 px-4 py-2.5 text-xs font-medium text-sw-text">
            Back
          </button>
        )}
        <button type="button" onClick={onNextStep}
          className="flex-1 rounded-full bg-earth-green px-4 py-2.5 text-xs font-medium text-white shadow-lg shadow-earth-green/20">
          Continue
        </button>
      </div>
    )}

    {isLastStep && (
      <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl bg-cream-secondary px-4 py-3 lg:hidden">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-sw-text-muted">Final step</p>
          <p className="mt-1 text-sm font-medium text-sw-text">Preview your blend, then add it to cart.</p>
        </div>
        {!isFirstStep && (
          <button type="button" onClick={onPrevStep}
            className="shrink-0 rounded-full border border-gray-200 px-4 py-2.5 text-xs font-medium text-sw-text">
            Back
          </button>
        )}
      </div>
    )}
  </motion.section>
);

export const StudioBuilder = (props) => {
  const { base, setBase, style, setStyle, spice, setSpice, salt, setSalt,
    oil, setOil, sourness, setSourness, texture, setTexture,
    ingredients, setIngredients, activeProfile, setActiveProfile,
    applyProfile, jarSize, setJarSize, pickleName, setPickleName,
    notes, setNotes, isFavorite, setIsFavorite, applyPreset,
    handleSave, handleAddToCart,
    mobileActiveStep, onNextStep, onPrevStep, isFirstStep, isLastStep } = props;

  // Hover info states — each section tracks what the user is mousing over
  const [presetInfo, setPresetInfo] = useState(null);
  const [styleInfo, setStyleInfo] = useState(null);
  const [textureInfo, setTextureInfo] = useState(null);
  const [ingredientInfo, setIngredientInfo] = useState(null);

  const handleSliderChange = (setter) => (v) => { setter(v[0]); setActiveProfile(null); };
  const toggleIngredient = (id) => {
    setIngredients((prev) => ({ ...prev, [id]: !prev[id] }));
    setActiveProfile(null);
  };

  // Fallbacks: when nothing is hovered, show the currently selected item's description
  const displayStyleInfo = styleInfo || REGIONAL_STYLES.find((s) => s.id === style);
  const displayTextureInfo = textureInfo || TEXTURES.find((t) => t.id === texture);

  return (
    <div className="space-y-5">
      {/* Quick Presets */}
      <motion.div
        {...sectionAnim}
        className={`${mobileActiveStep === 0 ? "block" : "hidden"} lg:block`}
        data-testid="quick-presets"
      >
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {QUICK_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => applyPreset(preset)}
              onMouseEnter={() => setPresetInfo(preset)}
              onMouseLeave={() => setPresetInfo(null)}
              data-testid={`preset-${preset.id}`}
              className="shrink-0 px-4 py-2.5 rounded-full text-xs font-medium bg-white border border-gray-200 text-sw-text-secondary hover:border-earth-green hover:text-earth-green transition-all whitespace-nowrap shadow-sm"
            >
              <ChefHat size={12} className="inline mr-1.5 -mt-0.5" />{preset.name}
            </button>
          ))}
        </div>
        <InfoShelf name={presetInfo?.name} description={presetInfo?.description} />
      </motion.div>

      {/* 1. Base */}
      <Section
        id="base" step={0}
        title="Choose Your Base"
        subtitle="The foundation of your pickle"
        icon={Cherry}
        onView={props.setActiveStep}
        isMobileActive={mobileActiveStep === 0}
        isFirstStep={isFirstStep} isLastStep={false}
        onNextStep={onNextStep} onPrevStep={onPrevStep}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {PICKLE_BASES.map((b) => (
            <button key={b.id} onClick={() => setBase(b.id)} data-testid={`base-${b.id}`}
              className={`p-3 rounded-2xl border text-left transition-all duration-300 ${base === b.id ? "border-earth-green bg-earth-green/5 shadow-sm" : "border-gray-100 hover:border-gray-200"}`}>
              <div className="w-6 h-6 rounded-full mb-2" style={{ backgroundColor: b.color + "25", border: `2px solid ${b.color}` }} />
              <p className="text-sm font-heading font-600 text-sw-text">{b.name}</p>
              <p className="text-[10px] text-sw-text-muted leading-tight mt-0.5">{b.description}</p>
              {b.region !== "Both" && (
                <span className="mt-1.5 inline-block text-[9px] uppercase tracking-[0.14em] font-medium px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: b.color + "18", color: b.color }}>
                  {b.region}
                </span>
              )}
            </button>
          ))}
        </div>
      </Section>

      {/* 2. Style */}
      <Section
        id="style" step={1}
        title="Regional Style"
        subtitle="Hover any style to learn what it means"
        icon={Sparkles}
        onView={props.setActiveStep}
        isMobileActive={mobileActiveStep === 1}
        isFirstStep={false} isLastStep={false}
        onNextStep={onNextStep} onPrevStep={onPrevStep}
      >
        <div className="flex flex-wrap gap-2">
          {REGIONAL_STYLES.map((s) => (
            <button
              key={s.id}
              onClick={() => setStyle(s.id)}
              onMouseEnter={() => setStyleInfo(s)}
              onMouseLeave={() => setStyleInfo(null)}
              data-testid={`style-${s.id}`}
              className={`px-4 py-2.5 rounded-full text-xs font-medium transition-all duration-300 ${style === s.id ? "bg-earth-green text-white shadow-md" : "bg-cream-secondary text-sw-text-secondary hover:bg-cream-tertiary"}`}
            >
              {s.name}
              {s.region !== "Both" && (
                <span className={`ml-1.5 text-[9px] uppercase tracking-[0.1em] opacity-70 ${style === s.id ? "text-white" : ""}`}>
                  {s.region === "Maharashtra" ? "MH" : "AP"}
                </span>
              )}
            </button>
          ))}
        </div>
        <InfoShelf name={displayStyleInfo?.name} description={displayStyleInfo?.description} />
      </Section>

      {/* 3. Taste */}
      <Section
        id="taste" step={2}
        title="Taste Profile"
        subtitle="Dial in your perfect flavor balance"
        icon={Flame}
        onView={props.setActiveStep}
        isMobileActive={mobileActiveStep === 2}
        isFirstStep={false} isLastStep={false}
        onNextStep={onNextStep} onPrevStep={onPrevStep}
      >
        <div className="space-y-6 swadishes-slider">
          {[
            { label: "Spice Level", value: spice, setter: setSpice, color: "#9F3E18", low: "Mild", high: "Fiery" },
            { label: "Salt Level", value: salt, setter: setSalt, color: "#2E5C5A", low: "Low", high: "Traditional" },
            { label: "Oil Level", value: oil, setter: setOil, color: "#D97706", low: "Light", high: "Rich" },
            { label: "Sourness", value: sourness, setter: setSourness, color: "#65A30D", low: "Subtle", high: "Punchy" },
          ].map((ctrl) => (
            <div key={ctrl.label}>
              <div className="flex justify-between items-center mb-2.5">
                <span className="text-sm font-medium text-sw-text-secondary">{ctrl.label}</span>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-cream-secondary text-sw-text-secondary">{getLevelLabel(ctrl.value)}</span>
              </div>
              <Slider value={[ctrl.value]} onValueChange={handleSliderChange(ctrl.setter)} max={100} step={5}
                data-testid={`studio-slider-${ctrl.label.toLowerCase().replace(/\s/g, "-")}`} />
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-sw-text-muted">{ctrl.low}</span>
                <span className="text-[10px] text-sw-text-muted">{ctrl.high}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 4. Texture */}
      <Section
        id="texture" step={3}
        title="Texture"
        subtitle="Hover any option to understand the cut"
        icon={Wind}
        onView={props.setActiveStep}
        isMobileActive={mobileActiveStep === 3}
        isFirstStep={false} isLastStep={false}
        onNextStep={onNextStep} onPrevStep={onPrevStep}
      >
        <div className="flex flex-wrap gap-2">
          {TEXTURES.map((t) => (
            <button
              key={t.id}
              onClick={() => setTexture(t.id)}
              onMouseEnter={() => setTextureInfo(t)}
              onMouseLeave={() => setTextureInfo(null)}
              data-testid={`texture-${t.id}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${texture === t.id ? "bg-earth-green text-white shadow-md" : "bg-cream-secondary text-sw-text-secondary hover:bg-cream-tertiary"}`}
            >
              {t.name}
            </button>
          ))}
        </div>
        <InfoShelf name={displayTextureInfo?.name} description={displayTextureInfo?.description} />
      </Section>

      {/* 5. Ingredients */}
      <Section
        id="ingredients" step={4}
        title="Ingredients"
        subtitle="Hover any ingredient to see what it does"
        icon={Droplets}
        onView={props.setActiveStep}
        isMobileActive={mobileActiveStep === 4}
        isFirstStep={false} isLastStep={false}
        onNextStep={onNextStep} onPrevStep={onPrevStep}
      >
        {INGREDIENT_CATEGORIES.map((cat) => {
          const items = STUDIO_INGREDIENTS.filter((i) => i.category === cat);
          if (items.length === 0) return null;
          return (
            <div key={cat} className="mb-4 last:mb-0">
              <p className="text-xs text-sw-text-muted uppercase tracking-wider mb-2">{cat}</p>
              <div className="flex flex-wrap gap-2">
                {items.map((ing) => (
                  <button
                    key={ing.id}
                    onClick={() => toggleIngredient(ing.id)}
                    onMouseEnter={() => setIngredientInfo(ing)}
                    onMouseLeave={() => setIngredientInfo(null)}
                    data-testid={`ing-${ing.id}`}
                    className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 border ${ingredients[ing.id] ? "bg-earth-green/10 border-earth-green text-earth-green" : "bg-cream-secondary/50 border-transparent text-sw-text-muted"}`}
                  >
                    {ingredients[ing.id] ? "+" : "-"} {ing.name}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        <InfoShelf name={ingredientInfo?.name} description={ingredientInfo?.description} />
      </Section>

      {/* 6. Smart Profile */}
      <Section
        id="profile" step={5}
        title="Wellness Profile"
        subtitle="Auto-adjust for dietary needs"
        icon={HeartPulse}
        onView={props.setActiveStep}
        isMobileActive={mobileActiveStep === 5}
        isFirstStep={false} isLastStep={false}
        onNextStep={onNextStep} onPrevStep={onPrevStep}
      >
        <div className="space-y-2.5">
          {PROFILES.map((profile) => {
            const Icon = profileIconMap[profile.icon];
            const isActive = activeProfile?.id === profile.id;
            return (
              <button key={profile.id} onClick={() => applyProfile(profile)} data-testid={`studio-profile-${profile.id}`}
                className={`w-full text-left p-3.5 rounded-2xl border transition-all duration-300 flex items-center gap-3 ${isActive ? "border-earth-green bg-earth-green/5 shadow-sm" : "border-gray-100 hover:border-gray-200"}`}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${profile.color}12` }}>
                  {Icon && <Icon size={17} style={{ color: profile.color }} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-heading font-600 text-sw-text">{profile.name}</p>
                  <p className="text-[11px] text-sw-text-muted">{profile.description}</p>
                </div>
                {isActive && <div className="w-2 h-2 rounded-full bg-earth-green shrink-0" />}
              </button>
            );
          })}
          {activeProfile && (
            <button onClick={() => setActiveProfile(null)} className="text-xs text-sw-text-muted hover:text-earth-green transition-colors flex items-center gap-1 mt-1">
              <RotateCcw size={12} /> Clear profile
            </button>
          )}
        </div>
      </Section>

      {/* 7. Jar Size */}
      <Section
        id="jar-size" step={6}
        title="Jar Size"
        subtitle="Pick your quantity"
        icon={null}
        onView={props.setActiveStep}
        isMobileActive={mobileActiveStep === 6}
        isFirstStep={false} isLastStep={false}
        onNextStep={onNextStep} onPrevStep={onPrevStep}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {JAR_SIZES.map((jar) => (
            <button key={jar.id} onClick={() => setJarSize(jar.id)} data-testid={`jar-${jar.id}`}
              className={`p-4 rounded-2xl border text-center transition-all duration-300 ${jarSize === jar.id ? "border-earth-green bg-earth-green/5 shadow-sm" : "border-gray-100 hover:border-gray-200"}`}>
              <p className="text-sm font-heading font-700 text-sw-text">{jar.name}</p>
              <p className="text-xs text-sw-text-muted mt-0.5">{jar.weight}</p>
              <p className="text-base font-heading font-700 text-earth-green mt-2">&#8377;{jar.price}</p>
              <p className="text-[10px] text-sw-text-muted mt-0.5">{jar.description}</p>
            </button>
          ))}
        </div>
      </Section>

      {/* 8. Personalize */}
      <Section
        id="personalize" step={7}
        title="Make It Yours"
        subtitle="Name your creation"
        icon={Heart}
        onView={props.setActiveStep}
        isMobileActive={mobileActiveStep === 7}
        isFirstStep={false} isLastStep={isLastStep}
        onNextStep={onNextStep} onPrevStep={onPrevStep}
      >
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-sw-text-secondary mb-1.5 block">Pickle Name</label>
            <input type="text" value={pickleName} onChange={(e) => setPickleName(e.target.value)}
              placeholder="e.g. Nani's Mango Fire" data-testid="pickle-name-input"
              className="w-full bg-cream-secondary/50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-sw-text placeholder:text-sw-text-muted/50 focus:outline-none focus:border-earth-green focus:ring-1 focus:ring-earth-green/20 transition-all" />
          </div>
          <div>
            <label className="text-xs font-medium text-sw-text-secondary mb-1.5 block">Personal Note (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Extra garlic, medium spice, for Dad's birthday" data-testid="pickle-notes-input" rows={2}
              className="w-full bg-cream-secondary/50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-sw-text placeholder:text-sw-text-muted/50 focus:outline-none focus:border-earth-green focus:ring-1 focus:ring-earth-green/20 transition-all resize-none" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-sw-text-secondary">Mark as Family Favorite</span>
            <Switch checked={isFavorite} onCheckedChange={setIsFavorite} data-testid="favorite-toggle" className="data-[state=checked]:bg-earth-green" />
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 border-t border-gray-100 pt-5 sm:flex-row">
          <button onClick={handleSave} data-testid="save-recipe-btn"
            className="flex-1 border border-earth-green text-earth-green rounded-full py-3 font-medium text-sm hover:bg-earth-green hover:text-white transition-all duration-300">
            Save Recipe
          </button>
          <button onClick={handleAddToCart} data-testid="add-to-cart-btn"
            className="flex-1 bg-earth-green text-white rounded-full py-3 font-medium text-sm hover:bg-earth-green-dark transition-all duration-300 shadow-lg shadow-earth-green/20">
            Add to Cart
          </button>
        </div>
      </Section>
    </div>
  );
};
